<script setup>
import { ref, onMounted, onBeforeUnmount, watch, nextTick } from 'vue';
import { RTVIEvent } from '@pipecat-ai/client-js';

const { $connect, $disconnect, $sendMessage, $micMute, $setupConnectUI, $setupUpdateStatusUI, $toast, $selectPhotoByIndex, $setupAppendTranscriptUI } = useNuxtApp();

// Reactive state
const connectionStatus = ref('Disconnected');
const isLoading = ref(false);
const debugLogs = ref([]);
const botAudio = ref(null);
const showTranscript = ref(false);
const isVisible = ref(false);
const isUserSpeaking = ref(false);
const uiState = ref('initial'); // 'initial', 'loading', 'connected', 'failed'
const isExpanded = ref(false);
const isMuted = ref(false);
const audioLevel = ref(0);
let audioContext = null;
let analyser = null;
let microphone = null;

const transcript = ref([]);
const transcriptContainer = ref(null);

// Auto scroll transcript to bottom when new messages arrive
watch(transcript, async () => {
    await nextTick();
    if (transcriptContainer.value) {
        transcriptContainer.value.scrollTop = transcriptContainer.value.scrollHeight;
    }
}, { deep: true });

// Function to log messages
const log = (message) => {
    debugLogs.value.push(`${new Date().toISOString()} - ${message}`);
    console.log(message);
};

watch(uiState, (newStatus, oldStatus) => {
    console.log('uiState changed:', oldStatus, '->', newStatus);
    console.log('isLoading:', isLoading.value);
    console.log('isExpanded:', isExpanded.value);
});

// Function to update connection status
const updateStatus = (status) => {
    console.log("Updating status and uiState, Before ::", "status:", status, "uiState:", uiState.value);

    // Special case for hangup vs unexpected disconnect
    // conncected -> HangUp = graceful disconnect
    // otherwise = unexpected disconnect
    if (status === 'HangUp') {
        isLoading.value = false;
        uiState.value = 'initial';
        isExpanded.value = false;
        console.log("Setting to initial state");
        return;
    } 

    connectionStatus.value = status;
    if (status === 'Connecting') {
        isLoading.value = true;
        uiState.value = 'loading';
        isExpanded.value = false;
        console.log("Setting to loading state");
    } else if (status === 'Offline' && uiState.value === 'loading') {
        isLoading.value = true;
        uiState.value = 'loading';
        isExpanded.value = false;
    } else if (status === 'Online') {
        isLoading.value = false;
        uiState.value = 'connected';
        setTimeout(() => {
            isExpanded.value = true;
        }, 100);
        console.log("Setting to connected state");
    } else if (status === 'Error') {
        isLoading.value = false;
        uiState.value = 'failed';
        isExpanded.value = false;
        console.log("Setting to failed state");
        $toast.error("Error connecting to voice assistant. Please try again.");
    } else {
        isLoading.value = false;
        uiState.value = 'initial';
        isExpanded.value = false;
        console.log("Setting to initial state");
    }
    console.log("After update ::", "status:", status, "uiState:", uiState.value);
    log(`Status: ${status}`);
};

// Function to setup audio track
const setupAudioTrack = (track) => {
    log('Setting up audio track');
    if (botAudio.value) {
        botAudio.value.srcObject = new MediaStream([track]);
    }
};

const handleFunctionCall = (func) => {
    console.log(`Function call received: ${func.function_name}`, func.args);
    if (func.function_name === 'select_photo_by_index') {
        $selectPhotoByIndex(func.args.index);
    }
};

// Function to handle user speaking state
const handleUserSpeaking = (isSpeaking) => {
    isUserSpeaking.value = isSpeaking;
};

// Function to handle microphone muting
const handleMuteToggle = () => {
    isMuted.value = !isMuted.value;
    $micMute(isMuted.value);
};

// Function to setup audio analysis
const setupAudioAnalysis = async () => {
    try {
        audioContext = new (window.AudioContext || window.webkitAudioContext)();
        analyser = audioContext.createAnalyser();
        analyser.fftSize = 256;

        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        microphone = audioContext.createMediaStreamSource(stream);
        microphone.connect(analyser);

        const dataArray = new Uint8Array(analyser.frequencyBinCount);

        const updateAudioLevel = () => {
            if (!analyser) return;

            analyser.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
            audioLevel.value = average / 128; // Normalize to 0-1

            // Consider user speaking if audio level is above threshold
            isUserSpeaking.value = audioLevel.value > 0.1;

            requestAnimationFrame(updateAudioLevel);
        };

        updateAudioLevel();
    } catch (error) {
        console.error('Error setting up audio analysis:', error);
    }
};

// Function to cleanup audio analysis
const cleanupAudioAnalysis = () => {
    if (microphone) {
        microphone.disconnect();
    }
    if (audioContext) {
        audioContext.close();
    }
    audioContext = null;
    analyser = null;
    microphone = null;
};

// Function to connect to the chatbot
const connect = async () => {
    try {
        console.log("Starting connection process");
        updateStatus('Connecting');

        // Setup audio analysis
        await setupAudioAnalysis();

        // Using dummy values for now
        $connect();

    } catch (error) {
        log(`Error connecting: ${error.message}`);
        updateStatus('Error');
    }
};

// Function to disconnect from the chatbot
const disconnect = async () => {
    console.log("Disconnecting, current uiState:", uiState.value);
    isExpanded.value = false;
    cleanupAudioAnalysis();
    $disconnect();
};

const sendMessage = () => {
    $sendMessage('Hello! How can you help me today?');
};

const appendTranscript = (newTranscript, role) => {

    console.log("Appending transcript: ", newTranscript, role);

    let type = '';
    switch (role) {
        case 'user':
            type = 'You';
            break;
        case 'ai':
            type = 'Livia';
            break;
        default:
            type = '';
            break;
    }

    transcript.value.push({
        role: type,
        content: newTranscript
    });
};

// Ensure chatbot initializes only on client-side
onMounted(() => {
    $setupConnectUI(connect);
    $setupUpdateStatusUI(updateStatus);
    $setupAppendTranscriptUI(appendTranscript);
    botAudio.value = document.createElement('audio');
    botAudio.value.autoplay = true;
    botAudio.value.playsInline = true;
    document.body.appendChild(botAudio.value);
});

// Cleanup before unmounting
onBeforeUnmount(() => {
    disconnect();
});

// Add toggleVisibility method
const toggleVisibility = (show) => {

    // Also disconnects the bot if it is connected
    if (isVisible.value) {
        disconnect();
    }

    isVisible.value = show;
};

// Expose the method to parent components
defineExpose({
    toggleVisibility
});
</script>

<template>
    <div class="fixed right-4 md:top-1/2 md:-translate-y-1/2 bottom-24 md:bottom-auto z-50" v-show="isVisible">
        <!-- Initial Connect Button -->
        <button v-if="uiState === 'initial' && !isLoading" @click="connect"
            class="w-12 h-12 rounded-full flex items-center justify-center bg-purple-600 text-white hover:bg-purple-700 shadow-lg transition-all duration-300"
            title="Connect to Assistant">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
        </button>

        <!-- Loading State -->
        <div v-if="isLoading"
            class="w-12 h-12 rounded-full flex items-center justify-center bg-amber-50 transition-all duration-300">
            <div class="flex space-x-1">
                <div class="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style="animation-delay: 0s"></div>
                <div class="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                <div class="w-2 h-2 bg-amber-500 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>
            </div>
        </div>

        <!-- Failed State -->
        <button v-if="uiState === 'failed'" @click="connect"
            class="w-12 h-12 rounded-full flex items-center justify-center bg-red-600 text-white hover:bg-red-700 shadow-lg transition-all duration-300"
            title="Retry Connection">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
        </button>

        <!-- Connected State with Expanded UI -->
        <div v-if="uiState === 'connected'"
            class="flex flex-col gap-2 bg-white rounded-full shadow-lg p-2 transition-all duration-300"
            :class="{ 'scale-0': !isExpanded, 'scale-100': isExpanded }">
            <!-- Status Indicator -->
            <div class="relative w-10 h-10 flex items-center justify-center rounded-full bg-green-100">
                <div class="w-3 h-3 rounded-full bg-green-600"></div>
            </div>

            <!-- Disconnect Button -->
            <button @click="disconnect"
                class="w-10 h-10 rounded-full flex items-center justify-center bg-red-50 text-red-600 hover:bg-red-100 transition-colors duration-200"
                title="Disconnect">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            <!-- Transcript Toggle -->
            <button @click="showTranscript = !showTranscript"
                class="w-10 h-10 rounded-full flex items-center justify-center bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors duration-200"
                :title="showTranscript ? 'Hide Transcript' : 'Show Transcript'">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
            </button>

            <!-- Mute Button -->
            <button @click="handleMuteToggle"
                class="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-200 relative"
                :class="[
                    isMuted ? 'bg-red-50 text-red-600 hover:bg-red-100' :
                        isUserSpeaking ? 'bg-purple-100 text-purple-600 hover:bg-purple-200 ring-2 ring-purple-400 ring-offset-2 animate-pulse' :
                            'bg-green-50 text-green-600 hover:bg-green-100'
                ]" :title="isMuted ? 'Unmute Microphone' : 'Mute Microphone'">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path v-if="!isMuted" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    <template v-else>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6" />
                    </template>
                </svg>
            </button>
        </div>

        <!-- Transcript Panel -->
        <div v-show="showTranscript && uiState === 'connected'"
            class="fixed right-20 md:top-1/2 md:-translate-y-1/2 bottom-24 md:bottom-auto bg-white p-4 rounded-lg shadow-lg w-80 max-h-[70vh] overflow-y-auto transition-transform duration-300"
            :class="{ 'translate-x-full': !isExpanded, 'translate-x-0': isExpanded }" ref="transcriptContainer">
            <div class="space-y-4">
                <div v-for="(message, index) in transcript" :key="index" class="flex flex-col">
                    <span class="text-sm font-medium"
                        :class="message.role === 'You' ? 'text-blue-600' : 'text-purple-600'">
                        {{ message.role }}:
                    </span>
                    <p class="text-gray-700 whitespace-pre-wrap">{{ message.content }}</p>
                </div>
            </div>
        </div>
    </div>
</template>